import React, { useState, useEffect, useCallback } from 'react';
import '@fontsource/playfair-display';
import '@fontsource/cinzel';
import '@fontsource/sora';
import { normalizeCategory } from './utils/stringUtils';
import { Post, ViewState, BlogState, DailyStat } from './types';
import { MOCK_POSTS } from './data';
import Header from './components/Header';
import PostCard from './components/PostCard';
// Lazy Loading PostView for initial load performance optimization (Code Splitting)
const PostView = React.lazy(() => import('./components/PostView'));
import Sidebar from './components/Sidebar';
// Lazy Loading AdminView for improved initial load speed
const AdminView = React.lazy(() => import('./components/AdminView'));
const Contact = React.lazy(() => import('./components/Contact'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));
import {
  initFirebase,
  fetchPostsFromDB,
  fetchStatsFromDB,
  fetchCategoriesFromDB,
  saveStatToDB,
  fetchSettingsFromDB,
  saveSettingsToDB,
  fetchSubscribersFromDB,
  saveSubscribersToDB,
  updatePostInDB,
  saveCategoriesToDB,
  likePostInDB,
  fetchPostById
} from './services/api';

const SYSTEM_CATEGORY = 'Uncategorized';

const DEFAULT_CONTACT_EMAIL = 'admin@noxicon.blog';

// Safe localStorage helper to prevent quota exceeded errors
const MAX_CACHED_POSTS = 30; // Limit cache to 30 posts to stay well under 5MB limit

const safeSetLocalStorage = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    // Quota exceeded - clear the problematic cache and retry
    console.warn(`localStorage quota exceeded for ${key}, clearing cache...`);
    try {
      localStorage.removeItem(key);
      localStorage.setItem(key, value);
      return true;
    } catch (e2) {
      console.error(`Failed to save to localStorage even after clearing: ${key}`);
      return false;
    }
  }
};

const cachePostsSafely = (posts: Post[]) => {
  // Only cache the most recent 30 posts to prevent quota issues
  // Strip out large content and ensure no heavy base64 images are cached
  const postsToCache = posts.slice(0, MAX_CACHED_POSTS).map(p => {
    const isBase64Query = p.imageUrl && p.imageUrl.startsWith('data:');
    return {
      ...p,
      content: p.content.substring(0, 500) + '...', // Truncate content for cache
      imageUrl: isBase64Query ? '' : p.imageUrl // Don't cache base64 images
    };
  });
  safeSetLocalStorage('noxicon_cache_posts', JSON.stringify(postsToCache));
};


const App: React.FC = () => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);

  const [siteSettings, setSiteSettings] = useState({
    contactEmail: DEFAULT_CONTACT_EMAIL,
    siteUrl: window.location.origin,
    apiKey: '',
    grokApiKey: ''
  });

  const [userLikedPosts, setUserLikedPosts] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('noxicon_user_likes');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Failed to load liked posts", e);
      return [];
    }
  });
  // Legacy password auth removed
  const [subscribers, setSubscribers] = useState<string[]>([]);

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [state, setState] = useState<BlogState>(() => {
    // Initial state setup to prevent CLS (Layout Shift) by setting correct view immediately
    const params = new URLSearchParams(window.location.search);
    const postId = params.get('post');
    const categoryParam = params.get('category');
    const path = window.location.pathname;

    let view: ViewState = 'home';
    if (path === '/admin') view = 'admin';
    else if (path === '/contact') view = 'contact';
    else if (path === '/privacy') view = 'privacy';
    else if (path === '/terms') view = 'terms';
    else if (path.startsWith('/post/')) view = 'post';
    else if (postId) view = 'post'; // Legacy support

    return {
      currentView: view,
      selectedPost: null,
      searchQuery: '',
      selectedCategory: categoryParam || 'All'
    };
  });

  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const [subscribeEmailInput, setSubscribeEmailInput] = useState('');
  const [isAutoProcessing, setIsAutoProcessing] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6; // Server-side pagination limit
  const [totalPosts, setTotalPosts] = useState(0); // Add totalPosts for numbered pagination
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Initial data loading
  useEffect(() => {
    const checkAuth = async () => {
      // Only check auth for admin routes to save resources and avoid CORS on home
      if (!window.location.pathname.startsWith('/admin')) {
        return;
      }

      try {
        const res = await fetch('/api/admin/me');
        if (res.ok) {
          const data = (await res.json()) as { authenticated: boolean; user?: { email: string; role: string } };
          if (data.authenticated && data.user) {
            setIsAdminAuthenticated(true);
            setSiteSettings(prev => ({ ...prev, contactEmail: data.user.email }));
            setToastMessage(null);
          } else {
            // If expressly not authenticated by server, ensure local state matches
            setIsAdminAuthenticated(false);
          }
        } else {
          // Error handling
          const errText = await res.text();
          console.error("Auth check failed status:", res.status, errText);
          try {
            const errJson = JSON.parse(errText);
            setToastMessage(`Auth Error: ${errJson.message || 'Unknown'} (Debug: ${JSON.stringify(errJson.debug)})`);
          } catch (e) {
            setToastMessage(`Auth Error (${res.status}): ${errText.slice(0, 100)}`);
          }
          setIsAdminAuthenticated(false);
        }
      } catch (e) {
        console.error("Auth check failed", e);
        // setToastMessage(`Network Error: ${e}`); // Suppressed to avoid unsightly errors on load
      }
    };

    const loadData = async () => {
      console.log("Connecting to Cloudflare API...");

      // 1. Priority: Instant load from local cache (Instant Render)
      const cachedPosts = localStorage.getItem('noxicon_cache_posts');
      const cachedCats = localStorage.getItem('noxicon_cache_categories');
      const cachedSettings = localStorage.getItem('noxicon_cache_settings');

      if (cachedPosts) {
        setPosts(JSON.parse(cachedPosts));
        // Don't set isDataLoaded here if we want to show loading for freshness, 
        // but if we want instant render, we keep it. 
        // However, the user complains about "No posts" flashing. 
        // If cache is empty, this block is skipped, isDataLoaded remains false.
        // If cache exists, isDataLoaded becomes true, posts show.
        // The issue is likely when cache is empty.
        // Let's remove the unconditional setIsDataLoaded(true) below.
      }
      if (cachedCats) setCategories(JSON.parse(cachedCats));
      if (cachedSettings) setSiteSettings(prev => ({ ...prev, ...JSON.parse(cachedSettings) }));

      // 2. Non-blocking: Auth check (Background)
      checkAuth().catch(e => console.error("Auth check background error", e));

      // 3. Non-blocking: Fast load specific post (Background)
      const params = new URLSearchParams(window.location.search);
      const directPostId = params.get('post');
      const rawPostSlug = window.location.pathname.startsWith('/post/') ? window.location.pathname.split('/post/')[1] : null;
      const pathPostSlug = rawPostSlug ? decodeURIComponent(rawPostSlug) : null;

      if (directPostId || pathPostSlug) {
        const identifier = (pathPostSlug || directPostId) as string;
        // Remove trailing slash and query string from URL
        const cleanIdentifier = identifier.split('?')[0].replace(/\/$/, '');

        fetchPostById(cleanIdentifier).then(post => {
          if (post) {
            setState(prev => ({
              ...prev,
              currentView: 'post',
              selectedPost: post
            }));
            // On direct URL access, allow immediate render after successful post load
            setIsDataLoaded(true);

            // If it was a legacy ?post=id link, or slug mismatches (shouldnt happen but good for SEO), 
            // redirect to clean URL
            if (directPostId || (post.slug && pathPostSlug !== post.slug)) {
              window.history.replaceState({ postId: post.id }, '', `/post/${post.slug || post.id}`);
            }

            setPosts(prev => {
              if (prev.find(p => p.id === post.id)) return prev;
              return [post, ...prev];
            });
          } else {
            // Post not found (404) - redirect to home
            console.warn("Post not found:", cleanIdentifier);
            setState(prev => ({ ...prev, currentView: 'home', selectedPost: null }));
            window.history.replaceState({}, '', '/');
            setIsDataLoaded(true);
          }
        }).catch(err => {
          // Network error etc - keep state and log only (allow user to retry)
          console.error("Fast-load failed:", err);
          setIsDataLoaded(true); // End loading state
        });
      }

      // 4. Non-blocking: Full data sync (Background - Deferred)
      setTimeout(() => {
        try {
          // Settings sync
          fetchSettingsFromDB().then(cloudSettings => {
            if (cloudSettings) {
              setSiteSettings(prev => ({ ...prev, ...cloudSettings }));
              localStorage.setItem('noxicon_cache_settings', JSON.stringify(cloudSettings));
            }
          }).catch(err => console.error("Settings sync failed", err));

          // Other data sync
          fetchStatsFromDB().then(s => s && s.length > 0 && setDailyStats(s)).catch(() => { });
          fetchCategoriesFromDB().then(c => {
            if (c && c.length > 0) {
              setCategories(c);
              localStorage.setItem('noxicon_cache_categories', JSON.stringify(c));
            }
          }).catch(() => { });

          // Only fetch subscribers for admin view
          if (window.location.pathname.startsWith('/admin')) {
            fetchSubscribersFromDB().then(s => s && s.length > 0 && setSubscribers(s)).catch(() => { });
          }
        } catch (e) {
          console.error("Deferred Sync error", e);
        }
      }, 800); // 800ms delay to prioritize LCP (Post Content) fetch
    };

    loadData();
  }, []);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(state.searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [state.searchQuery]);

  // Centralized Data Fetching (Server-Side)
  useEffect(() => {
    const fetchPosts = async () => {
      // If we are landing on a specific post, delay the list fetch to prioritize the single post content
      if (state.currentView === 'post') {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (currentPage > 1) setIsLoadingMore(true);

      try {
        const responseData = await fetchPostsFromDB({
          category: state.selectedCategory,
          search: debouncedSearchQuery,
          limit: postsPerPage,
          offset: (currentPage - 1) * postsPerPage
        });

        // Backend returns { posts: Post[], total: number } or Post[] (legacy)
        const newPosts = Array.isArray(responseData) ? responseData : responseData.posts || [];
        // Fallback: If total is 0 (legacy or error), use current posts length + offset + 1 (heuristic) 
        // or just use posts.length if it's a legacy array response.
        // Better: if it's the first page and we have 6 items, assume more? 
        // Actually, if we use SQL count, it should be correct.
        // But if total is 0, let's at least trust the array length.
        const total = Array.isArray(responseData) ? newPosts.length : (responseData.total || newPosts.length);

        setTotalPosts(total);
        setHasMore(newPosts.length === postsPerPage);

        setPosts(newPosts);
        if (currentPage === 1) {
          cachePostsSafely(newPosts);
        }
      } catch (e) {
        console.error("Failed to fetch posts", e);
      } finally {
        setIsLoadingMore(false);
        setIsDataLoaded(true);
      }
    };

    fetchPosts();
  }, [state.selectedCategory, debouncedSearchQuery, currentPage]);

  useEffect(() => {
    localStorage.setItem('noxicon_user_likes', JSON.stringify(userLikedPosts));
  }, [userLikedPosts]);

  useEffect(() => {
    // 1. Handle Path Routing (initial load)
    const path = window.location.pathname;
    if (path === '/admin') {
      setState(prev => ({ ...prev, currentView: 'admin', selectedPost: null }));
    } else if (path === '/contact') {
      setState(prev => ({ ...prev, currentView: 'contact', selectedPost: null }));
    } else if (path === '/privacy') {
      setState(prev => ({ ...prev, currentView: 'privacy', selectedPost: null }));
    } else if (path === '/terms') {
      setState(prev => ({ ...prev, currentView: 'terms', selectedPost: null }));
    }

    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 2500);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const emailToUnsubscribe = params.get('email');

    if (action === 'unsubscribe' && emailToUnsubscribe) {
      setSubscribers(prev => {
        if (prev.includes(emailToUnsubscribe)) {
          setToastMessage(`${emailToUnsubscribe} has been unsubscribed.`);
          const newList = prev.filter(email => email !== emailToUnsubscribe);
          saveSubscribersToDB(newList);
          return newList;
        }
        return prev;
      });
      window.history.replaceState({}, '', window.location.pathname);
    }

    const upsertMetaByName = (name: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      return tag;
    };

    const upsertMetaByProperty = (property: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      return tag;
    };

    let canonical = document.getElementById('canonical-link') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.id = 'canonical-link';
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }

    const robots = upsertMetaByName('robots');
    const description = upsertMetaByName('description');
    const twitterTitle = upsertMetaByName('twitter:title');
    const twitterDescription = upsertMetaByName('twitter:description');
    const twitterImage = upsertMetaByName('twitter:image');
    const twitterUrl = upsertMetaByName('twitter:url');

    const base = siteSettings.siteUrl ? siteSettings.siteUrl.replace(/\/$/, '') : window.location.origin;
    const defaultTitle = 'Noxicon | Dream Interpretation & Symbol Meanings';
    const defaultDescription = 'Explore the hidden meanings of your dreams. Noxicon offers in-depth dream interpretation, symbol analysis, and spiritual insights to help you understand what your dreams are telling you.';
    const defaultImage = `${base}/og-cover.png`;

    const ogTitle = upsertMetaByProperty('og:title');
    const ogDesc = upsertMetaByProperty('og:description');
    const ogUrl = upsertMetaByProperty('og:url');
    const ogImage = upsertMetaByProperty('og:image');
    const ogType = upsertMetaByProperty('og:type');
    const ogPublishedTime = upsertMetaByProperty('article:published_time');
    const ogModifiedTime = upsertMetaByProperty('article:modified_time');

    const setMetaBundle = (meta: {
      title: string;
      descriptionText: string;
      url: string;
      image: string;
      ogType: 'website' | 'article';
      robotsText: string;
      publishedTime?: string;
      modifiedTime?: string;
    }) => {
      document.title = meta.title;
      robots.setAttribute('content', meta.robotsText);
      description.setAttribute('content', meta.descriptionText);
      canonical!.href = meta.url;

      ogTitle.setAttribute('content', meta.title);
      ogDesc.setAttribute('content', meta.descriptionText);
      ogUrl.setAttribute('content', meta.url);
      ogImage.setAttribute('content', meta.image);
      ogType.setAttribute('content', meta.ogType);
      ogPublishedTime.setAttribute('content', meta.publishedTime || '');
      ogModifiedTime.setAttribute('content', meta.modifiedTime || '');

      twitterTitle.setAttribute('content', meta.title);
      twitterDescription.setAttribute('content', meta.descriptionText);
      twitterImage.setAttribute('content', meta.image);
      twitterUrl.setAttribute('content', meta.url);
    };

    const toIsoDate = (rawDate?: string) => {
      if (!rawDate) return new Date().toISOString();
      const normalized = rawDate.replace(/\./g, '-');
      const parsed = new Date(normalized);
      return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
    };

    const postImage = (state.selectedPost?.imageUrl && !state.selectedPost.imageUrl.startsWith('data:'))
      ? state.selectedPost.imageUrl
      : defaultImage;

    if (state.currentView === 'admin') {
      setMetaBundle({
        title: 'Admin Center | Noxicon',
        descriptionText: 'Noxicon admin dashboard page.',
        url: `${base}/admin`,
        image: defaultImage,
        ogType: 'website',
        robotsText: 'noindex, nofollow, noarchive'
      });
    } else if (state.currentView === 'home') {
      setMetaBundle({
        title: defaultTitle,
        descriptionText: defaultDescription,
        url: base,
        image: defaultImage,
        ogType: 'website',
        robotsText: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      });
    } else if (state.currentView === 'contact') {
      setMetaBundle({
        title: 'Contact | Noxicon',
        descriptionText: 'Contact Noxicon for partnerships, feedback, or editorial inquiries.',
        url: `${base}/contact`,
        image: defaultImage,
        ogType: 'website',
        robotsText: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      });
    } else if (state.currentView === 'post' && state.selectedPost) {
      const postUrl = `${base}/post/${state.selectedPost.slug || state.selectedPost.id}`;
      const descText = state.selectedPost.meta_description || state.selectedPost.metaDescription || state.selectedPost.excerpt || `Dream interpretation: ${state.selectedPost.title}. Explore the symbolism and meaning.`;

      setMetaBundle({
        title: `${state.selectedPost.title} | Noxicon`,
        descriptionText: descText.substring(0, 160),
        url: postUrl,
        image: postImage,
        ogType: 'article',
        robotsText: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        publishedTime: toIsoDate(state.selectedPost.date),
        modifiedTime: toIsoDate(state.selectedPost.lastUpdated || state.selectedPost.date),
      });
    } else if (state.currentView === 'privacy') {
      setMetaBundle({
        title: 'Privacy Policy | Noxicon',
        descriptionText: 'Privacy Policy for Noxicon.',
        url: `${base}/privacy`,
        image: defaultImage,
        ogType: 'website',
        robotsText: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      });
    } else if (state.currentView === 'terms') {
      setMetaBundle({
        title: 'Terms of Service | Noxicon',
        descriptionText: 'Terms of Service for Noxicon.',
        url: `${base}/terms`,
        image: defaultImage,
        ogType: 'website',
        robotsText: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      });
    }

    const schemaId = 'dynamic-ld-json';
    let schemaTag = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!schemaTag) {
      schemaTag = document.createElement('script');
      schemaTag.id = schemaId;
      schemaTag.type = 'application/ld+json';
      document.head.appendChild(schemaTag);
    }

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Noxicon',
      url: base,
      inLanguage: 'en-US',
      description: defaultDescription,
      publisher: {
        '@type': 'Organization',
        name: 'Noxicon',
        url: base,
        logo: {
          '@type': 'ImageObject',
          url: `${base}/logo.svg`
        }
      }
    };

    if (state.currentView === 'post' && state.selectedPost) {
      const postUrl = `${base}/post/${state.selectedPost.slug || state.selectedPost.id}`;
      const categoryName = state.selectedPost.category || 'Dream Interpretation';
      const postSchema = {
        '@context': 'https://schema.org',
        '@graph': [
          websiteSchema,
          {
            '@type': 'BlogPosting',
            headline: state.selectedPost.title,
            description: state.selectedPost.excerpt || `Dream interpretation: ${state.selectedPost.title}. Explore the symbolism and meaning.`,
            image: postImage,
            url: postUrl,
            mainEntityOfPage: postUrl,
            datePublished: toIsoDate(state.selectedPost.date),
            dateModified: toIsoDate(state.selectedPost.lastUpdated || state.selectedPost.date),
            author: {
              '@type': 'Organization',
              name: 'Noxicon'
            },
            publisher: {
              '@type': 'Organization',
              name: 'Noxicon',
              logo: {
                '@type': 'ImageObject',
                url: `${base}/logo.svg`
              }
            },
            articleSection: categoryName,
            keywords: (state.selectedPost.tags || []).join(', ')
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: base },
              { '@type': 'ListItem', position: 2, name: categoryName, item: `${base}/?category=${encodeURIComponent(categoryName)}` },
              { '@type': 'ListItem', position: 3, name: state.selectedPost.title, item: postUrl }
            ]
          },
          // FAQ Schema for "People Also Ask" rich results
          ...(() => {
            try {
              const raw = state.selectedPost.faq_items || state.selectedPost.faqItems;
              if (!raw) return [];
              const items: { question: string; answer: string }[] = typeof raw === 'string' ? JSON.parse(raw) : raw;
              if (!Array.isArray(items) || items.length === 0) return [];
              return [{
                '@type': 'FAQPage',
                mainEntity: items.map(faq => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: { '@type': 'Answer', text: faq.answer }
                }))
              }];
            } catch { return []; }
          })()
        ]
      };
      schemaTag.text = JSON.stringify(postSchema);
    } else {
      schemaTag.text = JSON.stringify(websiteSchema);
    }
  }, [state.currentView, state.selectedPost, siteSettings.siteUrl]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const postId = params.get('post');
    const path = window.location.pathname;
    const rawPostSlug = path.startsWith('/post/') ? path.split('/post/')[1] : null;
    const postSlug = rawPostSlug ? decodeURIComponent(rawPostSlug) : null;

    if ((postId || postSlug) && posts.length > 0 && !state.selectedPost) {
      const targetPost = posts.find(p => p.id === postId || (postSlug && (p.slug === postSlug || p.id === postSlug)));
      if (targetPost) {
        // Pass true to skipHistory to avoid pushing the same URL again
        setTimeout(() => handlePostClick(targetPost, true), 100);
      }
    }
  }, [posts.length, state.selectedPost]);

  // Handle Browser Navigation (Back/Forward)
  useEffect(() => {
    const handlePopState = async () => {
      const params = new URLSearchParams(window.location.search);
      const postId = params.get('post');
      const categoryParam = params.get('category');
      const path = window.location.pathname;

      let view: ViewState = 'home';
      if (path === '/admin') view = 'admin';
      else if (path === '/contact') view = 'contact';
      else if (path === '/privacy') view = 'privacy';
      else if (path === '/terms') view = 'terms';
      else if (path.startsWith('/post/')) view = 'post';
      else if (postId) view = 'post';

      // When navigating to post page, call API directly instead of depending on posts array
      if (view === 'post') {
        const rawSlug = path.startsWith('/post/') ? path.split('/post/')[1] : null;
        const identifier = rawSlug ? decodeURIComponent(rawSlug) : postId;
        if (identifier) {
          try {
            const post = await fetchPostById(identifier);
            if (post) {
              setState(prev => ({
                ...prev,
                currentView: 'post',
                selectedPost: post,
                selectedCategory: categoryParam || 'All'
              }));
              return;
            }
          } catch (err) {
            console.error("Failed to load post on navigation:", err);
          }
        }
        // If post load fails, redirect to home
        view = 'home';
      }

      setState(prev => ({
        ...prev,
        currentView: view,
        selectedPost: null,
        selectedCategory: categoryParam || 'All'
      }));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const recordVisit = useCallback((postId?: string) => {
    const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
    console.log(`[Analytics] Recording visit. Date: ${today}, PostId: ${postId || 'Global'}, NewVisitor? Checking...`);

    // Visitor Tracking Logic
    let isNewVisitor = false;
    let isNewPostVisitor = false;

    try {
      const visitorKey = `noxicon_visit_${today}`;
      const postVisitorKey = `noxicon_post_visit_${today}`;

      // Check for global site visit
      if (!localStorage.getItem(visitorKey)) {
        localStorage.setItem(visitorKey, 'true');
        isNewVisitor = true;
      }

      // Check for specific post visit
      if (postId) {
        const visitedPosts = JSON.parse(localStorage.getItem(postVisitorKey) || '[]');
        if (!visitedPosts.includes(postId)) {
          visitedPosts.push(postId);
          localStorage.setItem(postVisitorKey, JSON.stringify(visitedPosts));
          isNewPostVisitor = true;
        }
      }
    } catch (e) {
      console.warn("Storage error during visit tracking, falling back to heuristic", e);
      isNewVisitor = Math.random() > 0.7;
      isNewPostVisitor = isNewVisitor;
    }

    console.log(`[Analytics] Status - NewVisitor: ${isNewVisitor}, NewPostVisitor: ${isNewPostVisitor}`);


    // Referrer Analysis
    let referrer = 'Direct';
    if (document.referrer) {
      try {
        const url = new URL(document.referrer);
        const hostname = url.hostname.toLowerCase();
        if (hostname.includes('google')) referrer = 'Google';
        else if (hostname.includes('naver')) referrer = 'Naver';
        else if (hostname.includes('daum') || hostname.includes('kakao')) referrer = 'Daum/Kakao';
        else if (hostname.includes('bing')) referrer = 'Bing';
        else if (hostname.includes('facebook') || hostname.includes('instagram')) referrer = 'Social';
        else if (hostname.includes('twitter') || hostname.includes('x.com')) referrer = 'X (Twitter)';
        else if (hostname === window.location.hostname) referrer = 'Internal';
        else referrer = hostname;
      } catch (e) {
        referrer = 'Unknown';
      }
    }

    if (referrer === 'Internal' && !postId) return; // Don't count internal navigation as new global visit source

    setDailyStats(prev => {
      let newStats;
      const existing = prev.find(s => s.date === today);

      if (existing) {
        const newReferrers = { ...(existing.referrers || {}) };
        newReferrers[referrer] = (newReferrers[referrer] || 0) + 1;

        newStats = prev.map(s => s.date === today ? {
          ...s,
          views: s.views + 1,
          visitors: s.visitors + (isNewVisitor ? 1 : 0),
          referrers: newReferrers
        } : s);
      } else {
        const newDay = {
          date: today,
          views: 1,
          visitors: 1,
          referrers: { [referrer]: 1 }
        };
        newStats = [...prev, newDay].slice(-14);
      }

      console.log(`[Analytics] Sending to DB...`, { date: today, postId, views: 1, isNewVisitor });

      const targetStat = newStats.find(s => s.date === today);
      if (targetStat) {
        saveStatToDB({
          ...targetStat,
          postId,
          currentReferrer: referrer,
          isNewVisitor,
          isNewPostVisitor
        }).catch(e => console.error("[Analytics] Failed to save stat:", e));
      }
      return newStats;
    });
  }, []);

  // Analytics Tracking Trigger
  useEffect(() => {
    if (state.currentView === 'admin') return;

    if (state.currentView === 'post') {
      // Only record if we have a resolved post (avoids double counting during load)
      if (state.selectedPost) {
        recordVisit(state.selectedPost.id);
      }
    } else {
      recordVisit();
    }
  }, [state.currentView, state.selectedPost?.id, recordVisit]);

  const handleResetStats = () => {
    if (window.confirm('Are you sure you want to reset views and visitor stats to 0?')) {
      const today = new Date().toLocaleDateString('en-CA');
      setDailyStats([{ date: today, views: 0, visitors: 0 }]);
      setPosts(prev => prev.map(p => ({ ...p, views: 0 })));
      alert('Stats have been reset.');
    }
  };

  const handleNavigate = (view: ViewState) => {
    setState(prev => ({ ...prev, currentView: view, selectedPost: null }));

    // URL Updating Concept
    if (view === 'home') {
      window.history.pushState({}, '', '/');
    } else if (view === 'contact') {
      window.history.pushState({}, '', '/contact');
    } else if (view === 'admin') {
      window.history.pushState({}, '', '/admin');
    } else if (view === 'privacy') {
      window.history.pushState({}, '', '/privacy');
    } else if (view === 'terms') {
      window.history.pushState({}, '', '/terms');
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prefetchCache = React.useRef<Set<string>>(new Set());

  const prefetchPost = useCallback((post: Post) => {
    if (post.content || prefetchCache.current.has(post.id)) return;

    prefetchCache.current.add(post.id);

    fetchPostById(post.id).then(fetched => {
      if (fetched) {
        setPosts(prev => prev.map(p => {
          if (p.id === post.id) {
            // BE SELECTIVE: Do not overwrite p.imageUrl if fetched data might be stale from API cache
            // We prioritize the imageUrl from the list (which might have been optimistically updated)
            return {
              ...p,
              content: fetched.content,
              strategyReasoning: fetched.strategyReasoning,
              methodologyDisclosure: fetched.methodologyDisclosure,
              references: fetched.references || [],
              prevPost: fetched.prevPost,
              nextPost: fetched.nextPost
            };
          }
          return p;
        }));
        setState(prev => {
          if (prev.selectedPost?.id === post.id) {
            // Similarly merge for the selected post
            return {
              ...prev,
              selectedPost: {
                ...prev.selectedPost,
                content: fetched.content,
                strategyReasoning: fetched.strategyReasoning,
                methodologyDisclosure: fetched.methodologyDisclosure,
                references: fetched.references || [],
                prevPost: fetched.prevPost,
                nextPost: fetched.nextPost
              }
            };
          }
          return prev;
        });
      }
    }).catch(e => {
      prefetchCache.current.delete(post.id);
    });
  }, []);

  const handlePostClick = useCallback((post: Post, skipHistory?: boolean) => {
    // recordVisit is handled by useEffect based on state change


    // Optimistic cache update for views
    const optimisticPost = { ...post, views: (post.views || 0) + 1 };

    // 1. Optimistic Navigation: Navigate immediately with what we have
    // (Title, Excerpt, Image are already available)
    setState(prev => ({
      ...prev,
      currentView: 'post',
      selectedPost: optimisticPost
    }));

    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Only push state if not skipping (e.g. from back button or initial load)
    // Check for explicit boolean true to avoid event objects being interpreted as true
    if (skipHistory !== true) {
      const newUrl = `/post/${post.slug || post.id}`;
      window.history.pushState({ postId: post.id }, '', newUrl);
    }

    // 2. Background Content Fetch (Streaming)
    // If content is missing or suspiciously short (e.g. just "..." or valid empty string from list view), fetch full
    if (!post.content || post.content.length < 50) {
      fetchPostById(post.id).then(fetched => {
        if (fetched) {
          // BE SELECTIVE: Do not overwrite post.imageUrl with potentially stale data from API
          const fullPost: Post = {
            ...post, // Use current post (with potentially updated imageUrl)
            content: fetched.content,
            strategyReasoning: fetched.strategyReasoning,
            methodologyDisclosure: fetched.methodologyDisclosure,
            references: fetched.references || [],
            views: optimisticPost.views, // Keep optimistic views
            prevPost: fetched.prevPost,
            nextPost: fetched.nextPost
          };

          // Update the view if user is still on it
          setState(prev => {
            if (prev.selectedPost && prev.selectedPost.id === post.id) {
              return { ...prev, selectedPost: fullPost };
            }
            return prev;
          });

          // Update cache
          setPosts(prev => prev.map(p => p.id === post.id ? fullPost : p));

          // Sync View Count to DB
          // View counting is handled by recordVisit (saveStatToDB) which safely increments views without overwriting content
          // updatePostInDB(fullPost).catch(err => console.error("Failed to update view count", err));
        }
      });
    } else {
      // Content already exists
      // View counting is handled by recordVisit (saveStatToDB)
      // updatePostInDB(optimisticPost).catch(err => console.error("Failed to update view count", err));
    }
  }, [recordVisit]);

  const handleLikePost = useCallback((postId: string) => {
    const isLiked = userLikedPosts.includes(postId);
    const isLiking = !isLiked;

    setUserLikedPosts(prev => isLiking ? [...prev, postId] : prev.filter(id => id !== postId));

    // Update posts array
    setPosts(prev => prev.map(p => p.id === postId ? {
      ...p,
      likes: isLiking ? (p.likes || 0) + 1 : Math.max(0, (p.likes || 0) - 1)
    } : p));

    // Update selectedPost if it's the one being liked
    setState(prev => {
      if (prev.selectedPost && prev.selectedPost.id === postId) {
        return {
          ...prev,
          selectedPost: {
            ...prev.selectedPost,
            likes: isLiking ? (prev.selectedPost.likes || 0) + 1 : Math.max(0, (prev.selectedPost.likes || 0) - 1)
          }
        };
      }
      return prev;
    });

    likePostInDB(postId, isLiking).catch(err => console.error("Like failed to sync", err));
  }, [userLikedPosts]);

  const handleSharePost = useCallback(async (post: Post) => {
    const base = siteSettings.siteUrl ? siteSettings.siteUrl.replace(/\/$/, '') : window.location.origin;
    const shareUrl = `${base}/post/${post.slug || post.id}`;

    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setToastMessage('Shared successfully.');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setToastMessage('Link copied to clipboard.');
      }
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, shares: p.shares + 1 } : p));
    } catch (err) {
      if ((err as Error).name !== 'AbortError') console.error('Share error:', err);
    }
  }, [siteSettings.siteUrl]);

  const handleCategorySelect = (c: string) => {
    setState(p => ({ ...p, selectedCategory: c, currentView: 'home' }));
    setCurrentPage(1); // Reset to first page on category change
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const newUrl = c === 'All' ? '/' : `/?category=${encodeURIComponent(c)}`;
    window.history.pushState({}, '', newUrl);
  };
  const handleSearchChange = (q: string) => {
    setState(p => ({ ...p, searchQuery: q, currentView: 'home' }));
    setCurrentPage(1); // Reset to first page on search change
  };

  const handleAddPost = (newPost: Post) => setPosts(prev => {
    const newList = [newPost, ...prev];
    cachePostsSafely(newList);
    return newList;
  });
  const handleUpdatePost = (updatedPost: Post) => setPosts(prev => {
    const newList = prev.map(p => p.id === updatedPost.id ? updatedPost : p);
    cachePostsSafely(newList);
    return newList;
  });
  const handleDeletePost = (id: string) => setPosts(prev => {
    const newList = prev.filter(p => p.id !== id);
    cachePostsSafely(newList);
    return newList;
  });

  const handleAddCategory = async (cat: string) => {
    const newCategories = [...categories, cat];
    setCategories(newCategories);
    try {
      await saveCategoriesToDB(newCategories);
    } catch (e) {
      console.error("Failed to save category:", e);
      alert('Failed to save category.');
    }
  };

  const handleUpdateCategory = async (o: string, n: string) => {
    const newCategories = categories.map(c => c === o ? n : c);
    setCategories(newCategories);
    setPosts(p => p.map(post => post.category === o ? { ...post, category: n } : post));
    try {
      await saveCategoriesToDB(newCategories);
    } catch (e) {
      console.error("Failed to update category:", e);
    }
  };

  const handleDeleteCategory = async (cat: string) => {
    const newCategories = categories.filter(c => c !== cat);
    setCategories(newCategories);
    setPosts(p => p.map(post => post.category === cat ? { ...post, category: SYSTEM_CATEGORY } : post));
    try {
      await saveCategoriesToDB(newCategories);
    } catch (e) {
      console.error("Failed to delete category:", e);
      alert('Failed to delete category.');
    }
  };

  const handleImportData = (p: Post[], c: string[]) => {
    setPosts(p);
    setCategories(c);
    alert('Data loaded. Changes will be saved to DB upon publishing.');
  };


  const handleUpdateSettings = async (s: any) => {
    setSiteSettings(s);
    try {
      await saveSettingsToDB(s);
    } catch (e) {
      console.error("Failed to save settings:", e);
      alert('Failed to save settings.');
    }
  };
  const handleAdminLogout = () => { setIsAdminAuthenticated(false); handleNavigate('home'); };

  const handleSubscribeSubmit = async () => {
    if (!subscribeEmailInput || !subscribeEmailInput.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    if (subscribers.includes(subscribeEmailInput)) {
      alert('This email is already subscribed.');
      setSubscribeEmailInput('');
      setIsSubscribeModalOpen(false);
      return;
    }
    const newList = [...subscribers, subscribeEmailInput];
    setSubscribers(newList);
    await saveSubscribersToDB(newList);
    setSubscribeEmailInput('');
    setIsSubscribeModalOpen(false);
    setToastMessage('Subscription successful. Thank you!');
  };

  /* Legacy sitemap handling removed in favor of Server-Side Functions */

  // Loading screen is only used minimally during auto processing (layout is shown during data loading)
  if (isAutoProcessing) {
    return (
      <div className="min-h-screen bg-deep-void flex flex-col items-center justify-center p-10 font-['Inter',_sans-serif]">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4 shadow-[0_0_15px_rgba(79,70,229,0.4)]"></div>
        <h2 className="text-xl font-bold text-starlight-mist animate-pulse">Loading Noxicon...</h2>
      </div>
    );
  }

  const displayCategories = ['All', ...categories];

  // Server-Side Pagination removes client-side filtering
  // However, private posts are filtered once more on client side (in case API is public)
  const currentPosts = state.currentView === 'admin' ? posts : posts.filter(p => !p.isPrivate);
  const totalPages = Math.ceil(totalPosts / postsPerPage);



  return (
    <div className="min-h-screen flex flex-col bg-deep-void relative">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-cyan-accent/8 to-transparent" />
      <Header
        onNavigate={handleNavigate}
        currentView={state.currentView}
        searchQuery={state.searchQuery}
        onSearchChange={handleSearchChange}
        onSubscribeClick={() => setIsSubscribeModalOpen(true)}
      />

      {toastMessage && <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] bg-slate-900/95 border border-white/10 text-white px-8 py-3 rounded-2xl font-bold shadow-2xl animate-in fade-in backdrop-blur-lg">{toastMessage}</div>}

      {/* Subscribe Modal - Modern Mystical Theme */}
      {isSubscribeModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-deep-void/90 backdrop-blur-md animate-in fade-in duration-300 px-4">
          <div className="ui-shell p-8 md:p-12 w-full max-w-lg relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-accent/50 to-transparent"></div>
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-cyan-accent/10 rounded-full blur-3xl group-hover:bg-cyan-accent/20 transition-colors duration-700"></div>

            <button onClick={() => setIsSubscribeModalOpen(false)} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="text-center relative z-10">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10 text-cyan-accent">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              </div>
              <h3 className="text-3xl font-serif text-starlight-mist mb-3">Join Noxicon</h3>
              <p className="text-slate-400 mb-8 leading-relaxed">Get new dream symbol interpretations and weekly insight notes in your inbox.</p>

              <form onSubmit={handleSubscribeSubmit} className="space-y-4">
                <div className="relative group/input">
                  <input
                    type="email"
                    required
                    value={subscribeEmailInput}
                    onChange={(e) => setSubscribeEmailInput(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-deep-void border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-cyan-accent/50 transition-all text-starlight-mist placeholder:text-slate-700 text-center"
                    autoFocus
                  />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-cyan-accent group-focus-within/input:w-full transition-all duration-500"></div>
                </div>
                <button
                  type="submit"
                  disabled={isAutoProcessing}
                  className="w-full bg-cyan-accent text-deep-void font-bold py-4 rounded-2xl uppercase tracking-widest text-xs hover:bg-white transition-colors shadow-lg shadow-cyan-accent/20"
                >
                  {isAutoProcessing ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              <p className="mt-6 text-[10px] text-slate-600 uppercase tracking-widest">No spam. Unsubscribe anytime.</p>
            </div>
          </div>
        </div>
      )}

      <main className="flex-grow">
        {state.currentView === 'admin' ? (
          isAdminAuthenticated ? (
            <React.Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>}>
              <AdminView
                posts={posts} onAddPost={handleAddPost} onUpdatePost={handleUpdatePost} onDeletePost={handleDeletePost}
                onExit={handleAdminLogout} dailyStats={dailyStats} categories={categories}
                onAddCategory={handleAddCategory} onUpdateCategory={handleUpdateCategory} onDeleteCategory={handleDeleteCategory}
                onImportData={handleImportData}
                contactEmail={siteSettings.contactEmail} siteUrl={siteSettings.siteUrl} apiKey={siteSettings.apiKey} grokApiKey={siteSettings.grokApiKey} onUpdateSettings={handleUpdateSettings}
                onResetStats={handleResetStats} subscribers={subscribers} useCloudDB={true} totalPosts={totalPosts}
              />
            </React.Suspense>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
              <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
              <p className="mb-6 text-slate-600">You must be logged in via Cloudflare Access to view this page.</p>
              <button onClick={() => window.location.reload()} className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition">
                Check Login Status / Refresh
              </button>
            </div>
          )
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12 relative z-10">
            {state.currentView === 'home' && !state.searchQuery && state.selectedCategory === 'All' && currentPage === 1 && (
              <section className="mb-16 md:mb-20 text-center max-w-5xl mx-auto relative">
                <div className="absolute inset-x-0 top-6 h-64 bg-gradient-to-r from-cyan-accent/12 via-gold-highlight/8 to-cyan-accent/12 blur-3xl -z-10 rounded-full pointer-events-none"></div>
                <span className="ui-kicker">Dream Interpretation Archive</span>
                <h2 className="mt-5 text-4xl md:text-7xl lg:text-8xl font-black text-starlight-mist mb-7 tracking-tighter leading-[0.9] text-balance font-serif drop-shadow-2xl">
                  Read the Dream. <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-accent to-gold-highlight">Find the Meaning.</span>
                </h2>
                <p className="text-lg md:text-2xl text-slate-400 max-w-2xl mx-auto leading-relaxed font-light">
                  Symbol-by-symbol dream interpretations to help you understand the message behind last night's dream.
                </p>
              </section>
            )}
            <div id="post-grid" className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <section className="lg:col-span-8">
                {state.currentView === 'home' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                      {isDataLoaded && currentPosts.length > 0 ? (
                        currentPosts.map((post, index) => <PostCard key={post.id} post={post} onClick={handlePostClick} onPrefetch={prefetchPost} priority={index < 2} />)
                      ) : (
                        !isDataLoaded || isLoadingMore ? (
                          // PostCard Skeletons for Loading State
                          Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="ui-shell overflow-hidden flex flex-col h-full animate-pulse">
                              <div className="h-56 bg-white/5 w-full relative">
                                <span className="absolute top-4 left-4 bg-white/10 w-16 h-6 rounded-full"></span>
                              </div>
                              <div className="p-6 flex flex-col flex-grow space-y-4">
                                <div className="h-4 bg-white/10 rounded w-1/3"></div>
                                <div className="h-8 bg-white/10 rounded w-full"></div>
                                <div className="h-4 bg-white/10 rounded w-full"></div>
                                <div className="h-4 bg-white/10 rounded w-2/3"></div>
                                <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                                  <div className="h-6 w-24 bg-white/10 rounded"></div>
                                  <div className="h-6 w-16 bg-white/10 rounded"></div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-span-full text-center py-20 text-slate-400 font-bold">
                            No search results or posts found.
                          </div>
                        )
                      )}
                    </div>

                    {totalPages > 1 && (
                      <nav className="mt-12 flex justify-center items-center space-x-2" aria-label="Pagination">
                        <button
                          onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          disabled={currentPage === 1}
                          className="px-4 py-2 rounded-xl text-sm font-bold border border-white/10 bg-mid-night-blue/80 text-slate-400 hover:bg-cyan-900/30 hover:border-cyan-500 hover:text-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          aria-label="Previous Page"
                        >
                          Prev
                        </button>

                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                            })
                            .reduce<(number | string)[]>((acc, page, idx, arr) => {
                              if (idx > 0 && page - (arr[idx - 1] as number) > 1) {
                                acc.push('...');
                              }
                              acc.push(page);
                              return acc;
                            }, [])
                            .map((item, idx) => (
                              item === '...' ? (
                                <span key={`ellipsis-${idx}`} className="px-2 text-slate-600">...</span>
                              ) : (
                                <button
                                  key={item}
                                  onClick={() => { setCurrentPage(item as number); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                  aria-current={currentPage === item ? 'page' : undefined}
                                  className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${currentPage === item
                                    ? 'bg-cyan-accent text-deep-void shadow-md shadow-cyan-900/50'
                                    : 'bg-mid-night-blue/80 text-slate-400 border border-white/10 hover:bg-cyan-900/30 hover:border-cyan-500 hover:text-cyan-400'
                                    }`}
                                >
                                  {item}
                                </button>
                              )
                            ))}
                        </div>

                        <button
                          onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 rounded-xl text-sm font-bold border border-white/10 bg-mid-night-blue/80 text-slate-400 hover:bg-cyan-900/30 hover:border-cyan-500 hover:text-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                          aria-label="Next Page"
                        >
                          Next
                        </button>
                      </nav>
                    )}
                  </>
                )}
                {state.currentView === 'contact' && (
                  <React.Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>}>
                    <Contact contactEmail={siteSettings.contactEmail} />
                  </React.Suspense>
                )}
                {state.currentView === 'privacy' && (
                  <React.Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>}>
                    <PrivacyPolicy />
                  </React.Suspense>
                )}
                {state.currentView === 'terms' && (
                  <React.Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>}>
                    <TermsOfService />
                  </React.Suspense>
                )}
                {state.currentView === 'post' && (
                  state.selectedPost ? (
                    <React.Suspense fallback={
                      <div className="max-w-4xl mx-auto py-12 px-4 animate-pulse">
                        <div className="h-8 bg-white/10 rounded w-1/4 mb-8"></div>
                        <div className="aspect-video md:aspect-[21/9] bg-white/5 rounded-[2rem] md:rounded-[2.5rem] mb-8 md:mb-12 border border-white/5"></div>
                        <div className="h-12 bg-white/10 rounded w-3/4 mb-6"></div>
                        <div className="h-4 bg-white/5 rounded w-full mb-4"></div>
                        <div className="h-4 bg-white/5 rounded w-full mb-4"></div>
                        <div className="h-4 bg-white/5 rounded w-2/3 mb-4"></div>
                      </div>
                    }>
                      <PostView post={state.selectedPost} isAdmin={isAdminAuthenticated} prevPost={state.selectedPost.prevPost} nextPost={state.selectedPost.nextPost} onBack={() => handleNavigate('home')} onLike={() => handleLikePost(state.selectedPost!.id)} onShare={() => handleSharePost(state.selectedPost!)} onNavigateToPost={handlePostClick} userLikedPosts={userLikedPosts} />
                    </React.Suspense>
                  ) : (
                    // Skeleton for Initial Post Load (prevent CLS)
                    <div className="max-w-4xl mx-auto py-12 px-4 animate-pulse">
                      <div className="h-8 bg-white/10 rounded w-1/4 mb-8"></div>
                      <div className="aspect-video md:aspect-[21/9] bg-white/5 rounded-[2rem] md:rounded-[2.5rem] mb-8 md:mb-12 border border-white/5"></div>
                      <div className="h-12 bg-white/10 rounded w-3/4 mb-6"></div>
                      <div className="h-4 bg-white/5 rounded w-full mb-4"></div>
                      <div className="h-4 bg-white/5 rounded w-full mb-4"></div>
                      <div className="h-4 bg-white/5 rounded w-2/3 mb-4"></div>
                    </div>
                  )
                )}
              </section>
              {(state.currentView === 'home' || state.currentView === 'post') && (
                <Sidebar categories={displayCategories} selectedCategory={state.selectedCategory} onCategorySelect={handleCategorySelect} topPosts={[...posts].sort((a, b) => b.views - a.views)} onPostClick={handlePostClick} />
              )}
            </div>
          </div>
        )
        }
      </main >

      <footer className="bg-deep-void border-t border-white/10 py-10 mt-20 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-cyan-accent/35 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center cursor-pointer group" onClick={() => handleNavigate('home')}>
            <div className="bg-transparent p-0 mr-3 transition-transform duration-700 group-hover:rotate-180 text-slate-500 group-hover:text-starlight-mist"><svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1-8.313-12.454z" />
              <path d="M17 3.5a.5.5 0 0 1 .5.5v1.5h1.5a.5.5 0 0 1 0 1h-1.5v1.5a.5.5 0 0 1-1 0V6.5h-1.5a.5.5 0 0 1 0-1h1.5V4a.5.5 0 0 1 .5-.5z" />
            </svg></div>
            <span className="text-xl font-bold text-slate-500 group-hover:text-starlight-mist font-cinzel tracking-[0.2em] uppercase transition-colors">Noxicon</span>
          </div>
          <p className="text-[10px] text-slate-600 font-bold uppercase tracking-[0.2em] text-center">© 2026 Noxicon. Dream interpretation and symbol insights.</p>
          <div className="flex items-center space-x-8">
            <button onClick={() => handleNavigate('privacy')} className="text-[10px] font-bold text-slate-500 hover:text-cyan-accent uppercase tracking-widest cursor-pointer transition-colors">Privacy</button>
            <button onClick={() => handleNavigate('terms')} className="text-[10px] font-bold text-slate-500 hover:text-cyan-accent uppercase tracking-widest cursor-pointer transition-colors">Terms</button>
            <button onClick={() => handleNavigate('contact')} className="text-[10px] font-bold text-slate-500 hover:text-cyan-accent uppercase tracking-widest cursor-pointer transition-colors">Contact</button>
          </div>
        </div>
      </footer>
    </div >
  );
};

export default App;
